import { type Bounds } from '@/domain/geometry';

import { type Camera, type Viewport, visibleBounds, worldToScreen } from './Camera';

/**
 * The infinite grid and coordinate axes.
 *
 * The grid is **adaptive**: its spacing in world units changes with zoom so that the
 * on-screen spacing always stays within a comfortable band. A fixed world spacing would
 * either dissolve into a grey wash when zoomed out or vanish entirely when zoomed in —
 * and drawing a fixed *screen* spacing instead would make the grid meaningless as a
 * measuring aid, because grid lines would no longer sit at round world coordinates.
 */

export interface GridStyle {
  readonly minorColor: string;
  readonly majorColor: string;
  readonly axisColor: string;
  readonly minorWidth: number;
  readonly majorWidth: number;
  readonly axisWidth: number;
  /** How many minor divisions make one major division. */
  readonly majorEvery: number;
  /** Target on-screen spacing, in CSS pixels, for a minor division. */
  readonly targetSpacing: number;
}

export const DEFAULT_GRID_STYLE: GridStyle = Object.freeze({
  minorColor: 'rgba(120, 130, 150, 0.18)',
  majorColor: 'rgba(120, 130, 150, 0.38)',
  axisColor: 'rgba(90, 100, 120, 0.75)',
  minorWidth: 1,
  majorWidth: 1,
  axisWidth: 1.5,
  majorEvery: 5,
  targetSpacing: 24,
});

/**
 * Which parts of the backdrop are drawn.
 *
 * Kept apart from `GridStyle` because the two answer different questions. A style says how a
 * thing looks *whenever* it is drawn and belongs to the build; this says whether it is drawn
 * *right now* and belongs to the user, who turns the axes off for a minute to read a figure
 * and turns them back on. Folding a transient view choice into a frozen style constant would
 * make "hide the axes" indistinguishable from "this build has no axes".
 */
export interface GridVisibility {
  /** The two heavier lines through the world origin. */
  readonly axes: boolean;
}

export const DEFAULT_GRID_VISIBILITY: GridVisibility = Object.freeze({ axes: true });

/**
 * Chooses a world-space spacing from the 1–2–5 sequence.
 *
 * 1, 2, 5, 10, 20, 50, … is the standard choice for measuring scales because every step
 * subdivides cleanly in decimal: a student reading "three and a half squares" gets a
 * round number. A power-of-two ladder zooms just as smoothly but produces spacings like
 * 0.0625 that nobody can read off a diagram.
 */
export function chooseSpacing(zoom: number, targetSpacing: number): number {
  const rawWorldSpacing = targetSpacing / zoom;
  const magnitude = 10 ** Math.floor(Math.log10(rawWorldSpacing));
  const normalized = rawWorldSpacing / magnitude;

  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

/**
 * Paints the grid.
 *
 * Drawn in **screen space** with an identity transform rather than in world space under
 * the camera matrix. Two reasons, both of which show up immediately on screen:
 *
 * - Line width would otherwise be scaled by zoom, so grid lines would thicken as the
 *   user zooms in and disappear as they zoom out.
 * - Rounding each line to a whole device pixel keeps it crisp; a transformed 1px line
 *   lands on a half-pixel and renders as a 2px blur.
 *
 * All lines of one weight are drawn in a single path, so the cost is three `stroke`
 * calls regardless of how many lines are visible.
 *
 * With the axes hidden, the lines through the origin are not dropped — they fall back to the
 * weight their coordinate earns, which for zero is always major. Dropping them would leave a
 * cross-shaped gap in the grid, and a gap reads as a defect rather than as a setting.
 */
export function paintGrid(
  context: CanvasRenderingContext2D,
  camera: Camera,
  viewport: Viewport,
  style: GridStyle = DEFAULT_GRID_STYLE,
  visibility: GridVisibility = DEFAULT_GRID_VISIBILITY
): void {
  const spacing = chooseSpacing(camera.zoom, style.targetSpacing);
  const visible: Bounds = visibleBounds(camera, viewport);
  const m = worldToScreen(camera, viewport);

  const firstX = Math.floor(visible.minX / spacing) * spacing;
  const lastX = Math.ceil(visible.maxX / spacing) * spacing;
  const firstY = Math.floor(visible.minY / spacing) * spacing;
  const lastY = Math.ceil(visible.maxY / spacing) * spacing;

  const major = spacing * style.majorEvery;

  const minorPath = new Path2D();
  const majorPath = new Path2D();
  const axisPath = new Path2D();

  // `+ 0.5` centres a 1px stroke on the pixel instead of straddling two.
  const snap = (value: number): number => Math.round(value) + 0.5;

  const isMultipleOf = (value: number, of: number): boolean =>
    Math.abs(value / of - Math.round(value / of)) < 1e-6;

  const isAxis = (value: number): boolean => visibility.axes && Math.abs(value) < spacing / 2;

  for (let x = firstX; x <= lastX; x += spacing) {
    const screenX = snap(m.a * x + m.e);
    const path = isMultipleOf(x, major) ? majorPath : minorPath;
    const target = isAxis(x) ? axisPath : path;
    target.moveTo(screenX, 0);
    target.lineTo(screenX, viewport.height);
  }

  for (let y = firstY; y <= lastY; y += spacing) {
    const screenY = snap(m.d * y + m.f);
    const path = isMultipleOf(y, major) ? majorPath : minorPath;
    const target = isAxis(y) ? axisPath : path;
    target.moveTo(0, screenY);
    target.lineTo(viewport.width, screenY);
  }

  context.save();
  context.lineCap = 'butt';

  context.strokeStyle = style.minorColor;
  context.lineWidth = style.minorWidth;
  context.stroke(minorPath);

  context.strokeStyle = style.majorColor;
  context.lineWidth = style.majorWidth;
  context.stroke(majorPath);

  if (visibility.axes) {
    context.strokeStyle = style.axisColor;
    context.lineWidth = style.axisWidth;
    context.stroke(axisPath);
  }

  context.restore();
}
